import { normalizeResourceRequirements, RESOURCE_REQUIREMENT_FIELDS, resourceRequirementToReservationUnit, } from '../resource-requirements';
// =============================================================================
// Resource Reservation Schema Version
// =============================================================================
/** Current schema version for ResolvedResourceReservation. Bump when fields change. */
export const RESOURCE_RESERVATION_VERSION = 3;
export const LEGACY_VM_SIZE_WORKLOAD_ADAPTER = 'legacy-vm-size-workload';
export const LEGACY_VM_SIZE_WORKLOAD_ADAPTER_VERSION = 2;
// =============================================================================
// Platform Defaults (bottom of the precedence chain)
// =============================================================================
/** Platform default resource requirements — used when no layer specifies a value. */
export const PLATFORM_RESOURCE_DEFAULTS = {
    minVcpu: 2,
    minMemoryGb: 4,
    minDiskGb: 40,
    exclusiveNode: false,
};
/**
 * Compatibility defaults for deprecated small/medium/large values.
 *
 * These are workload slices, not historical whole-VM shapes. Deployments can
 * override them through persisted capacity-pool settings or environment
 * fallback; the version/provenance is persisted on resolved reservations.
 */
export const DEFAULT_LEGACY_VM_SIZE_WORKLOAD_REQUIREMENTS = {
    small: {
        minVcpu: 0.625,
        minMemoryGb: 1.125,
        minDiskGb: 13,
        exclusiveNode: false,
    },
    medium: {
        minVcpu: 2,
        minMemoryGb: 3.625,
        minDiskGb: 40,
        exclusiveNode: false,
    },
    large: {
        minVcpu: 4,
        minMemoryGb: 7.625,
        minDiskGb: 80,
        exclusiveNode: false,
    },
};
/** Full capacity per VM size per provider. */
export const PROVIDER_VM_CAPACITY = {
    hetzner: {
        small: { vcpu: 2, ramGb: 4, storageGb: 40 },
        medium: { vcpu: 4, ramGb: 8, storageGb: 80 },
        large: { vcpu: 8, ramGb: 16, storageGb: 160 },
    },
    scaleway: {
        small: { vcpu: 3, ramGb: 4, storageGb: 40 },
        medium: { vcpu: 4, ramGb: 12, storageGb: 120 },
        large: { vcpu: 8, ramGb: 32, storageGb: 600 },
    },
    gcp: {
        small: { vcpu: 1, ramGb: 4, storageGb: 50 },
        medium: { vcpu: 2, ramGb: 8, storageGb: 50 },
        large: { vcpu: 4, ramGb: 16, storageGb: 50 },
    },
    vultr: {
        small: { vcpu: 2, ramGb: 4, storageGb: 80 },
        medium: { vcpu: 4, ramGb: 8, storageGb: 160 },
        large: { vcpu: 6, ramGb: 16, storageGb: 320 },
    },
    infomaniak: {
        small: { vcpu: 2, ramGb: 4, storageGb: 20 },
        medium: { vcpu: 4, ramGb: 8, storageGb: 20 },
        large: { vcpu: 8, ramGb: 16, storageGb: 20 },
    },
    digitalocean: {
        small: { vcpu: 2, ramGb: 4, storageGb: 80 },
        medium: { vcpu: 4, ramGb: 8, storageGb: 160 },
        large: { vcpu: 8, ramGb: 16, storageGb: 320 },
    },
    upcloud: {
        small: { vcpu: 2, ramGb: 4, storageGb: 50 },
        medium: { vcpu: 4, ramGb: 8, storageGb: 80 },
        large: { vcpu: 8, ramGb: 16, storageGb: 160 },
    },
};
/** Default capacity when provider is unknown. Uses Hetzner as baseline. */
export const DEFAULT_VM_CAPACITY = PROVIDER_VM_CAPACITY.hetzner;
// =============================================================================
// VM Size Selection from Resource Requirements
// =============================================================================
/**
 * Given resolved resource requirements, pick the smallest VM size that satisfies
 * them for the given provider. Returns 'large' if nothing fits (best-effort).
 */
export function selectVmSizeForRequirements(requirements, provider = 'hetzner') {
    const capacities = PROVIDER_VM_CAPACITY[provider] ?? DEFAULT_VM_CAPACITY;
    const sizes = ['small', 'medium', 'large'];
    for (const size of sizes) {
        const cap = capacities[size];
        if (cap.vcpu >= requirements.minVcpu &&
            cap.ramGb >= requirements.minMemoryGb &&
            cap.storageGb >= requirements.minDiskGb) {
            return size;
        }
    }
    return 'large'; // best-effort fallback
}
/**
 * Resolve resource requirements from the precedence chain.
 * Each field is independently resolved: the first layer that defines a field wins.
 * Any field not set by any layer falls back to PLATFORM_RESOURCE_DEFAULTS.
 *
 * The `source` field on the output records the highest-priority layer that
 * contributed at least one field. If no layer contributes anything, source is 'platform'.
 */
export function resolveResourceReservation(input, ids = {}, options = {}) {
    const platformDefaults = validateResourceRequirementsLayer('platform', 'platform', options.platformDefaults ?? PLATFORM_RESOURCE_DEFAULTS, { requireAllFields: true });
    const legacyWorkloadMapping = options.legacyWorkloadMapping
        ? validateLegacyWorkloadMapping(options.legacyWorkloadMapping)
        : DEFAULT_LEGACY_VM_SIZE_WORKLOAD_REQUIREMENTS;
    const compatibilityAdapterVersion = options.compatibilityAdapterVersion ?? LEGACY_VM_SIZE_WORKLOAD_ADAPTER_VERSION;
    const layers = [
        {
            source: 'task',
            sourceId: ids.taskId ?? '',
            requirements: input.task,
            legacyVmSize: options.legacyVmSizes?.task,
        },
        {
            source: 'trigger',
            sourceId: ids.triggerId ?? '',
            requirements: input.trigger,
            legacyVmSize: options.legacyVmSizes?.trigger,
        },
        {
            source: 'skill',
            sourceId: ids.skillId ?? '',
            requirements: input.skill,
            legacyVmSize: options.legacyVmSizes?.skill,
        },
        {
            source: 'agent-profile',
            sourceId: ids.agentProfileId ?? '',
            requirements: input.agentProfile,
            legacyVmSize: options.legacyVmSizes?.['agent-profile'],
        },
        {
            source: 'project',
            sourceId: ids.projectId ?? '',
            requirements: input.project,
            legacyVmSize: options.legacyVmSizes?.project,
        },
        {
            source: 'user',
            sourceId: ids.userId ?? '',
            requirements: input.user,
            legacyVmSize: options.legacyVmSizes?.user,
        },
    ];
    const resolved = {};
    const provenance = {};
    const diagnostics = [];
    const seen = new Set();
    let winningSource = 'platform';
    let winningSourceId = 'platform';
    let firstWinnerFound = false;
    const recordLayerContribution = (layer, req) => {
        for (const field of RESOURCE_REQUIREMENT_FIELDS) {
            if (seen.has(field))
                continue;
            const fieldValue = req.requirements[field];
            if (fieldValue !== undefined) {
                resolved[field] = fieldValue;
                seen.add(field);
                provenance[field] = req.provenance[field];
                if (!firstWinnerFound) {
                    winningSource = layer.source;
                    winningSourceId = layer.sourceId;
                    firstWinnerFound = true;
                }
            }
        }
    };
    for (const layer of layers) {
        const explicit = validatedExplicitLayer(layer);
        if (explicit)
            recordLayerContribution(layer, explicit);
        const legacy = validatedLegacyLayer(layer, {
            mapping: legacyWorkloadMapping,
            adapterVersion: compatibilityAdapterVersion,
            diagnostics,
        });
        if (legacy)
            recordLayerContribution(layer, legacy);
    }
    // Fill remaining fields from platform defaults
    for (const field of RESOURCE_REQUIREMENT_FIELDS) {
        if (!seen.has(field)) {
            resolved[field] = platformDefaults[field];
            provenance[field] = {
                source: 'platform',
                sourceId: 'platform',
                value: platformDefaults[field],
            };
        }
    }
    const minVcpu = resolved['minVcpu'];
    const minMemoryGb = resolved['minMemoryGb'];
    const minDiskGb = resolved['minDiskGb'];
    const cpuMillis = resourceRequirementToReservationUnit('minVcpu', minVcpu);
    const memoryMb = resourceRequirementToReservationUnit('minMemoryGb', minMemoryGb);
    const diskMb = resourceRequirementToReservationUnit('minDiskGb', minDiskGb);
    return {
        cpuMillis,
        memoryMb,
        diskMb,
        exclusiveNode: resolved['exclusiveNode'],
        source: winningSource,
        sourceId: winningSourceId,
        version: RESOURCE_RESERVATION_VERSION,
        fieldProvenance: provenance,
        diagnostics,
    };
}
function validatedExplicitLayer(layer) {
    const explicit = validateResourceRequirementsLayer(layer.source, layer.sourceId, layer.requirements);
    if (!explicit)
        return null;
    const provenance = {};
    let hasValue = false;
    for (const field of RESOURCE_REQUIREMENT_FIELDS) {
        const explicitValue = explicit[field];
        if (explicitValue === undefined)
            continue;
        provenance[field] = { source: layer.source, sourceId: layer.sourceId, value: explicitValue };
        hasValue = true;
    }
    return hasValue ? { requirements: explicit, provenance } : null;
}
function validatedLegacyLayer(layer, options) {
    const provenance = {};
    const merged = {};
    if (!layer.legacyVmSize)
        return null;
    const legacyDefaults = options.mapping[layer.legacyVmSize];
    for (const field of RESOURCE_REQUIREMENT_FIELDS) {
        const legacyValue = legacyDefaults[field];
        setNormalizedResourceRequirementField(merged, field, legacyValue);
        provenance[field] = legacyFieldProvenance(layer, legacyValue, options.adapterVersion);
    }
    options.diagnostics.push(`${layer.source}:${layer.legacyVmSize}:mapped-by-${LEGACY_VM_SIZE_WORKLOAD_ADAPTER}-v${options.adapterVersion}`);
    return { requirements: merged, provenance };
}
function setNormalizedResourceRequirementField(target, field, value) {
    if (field === 'exclusiveNode') {
        target.exclusiveNode = value;
        return;
    }
    target[field] = value;
}
function legacyFieldProvenance(layer, value, adapterVersion) {
    return {
        source: layer.source,
        sourceId: layer.sourceId,
        value,
        compatibility: {
            adapter: LEGACY_VM_SIZE_WORKLOAD_ADAPTER,
            version: adapterVersion,
            legacyVmSize: layer.legacyVmSize,
        },
    };
}
function validateLegacyWorkloadMapping(mapping) {
    return {
        small: validateResourceRequirementsLayer('platform', 'legacy:small', mapping.small, {
            requireAllFields: true,
        }),
        medium: validateResourceRequirementsLayer('platform', 'legacy:medium', mapping.medium, {
            requireAllFields: true,
        }),
        large: validateResourceRequirementsLayer('platform', 'legacy:large', mapping.large, {
            requireAllFields: true,
        }),
    };
}
function validateResourceRequirementsLayer(source, sourceId, requirements, options = {}) {
    if (!requirements)
        return undefined;
    try {
        return normalizeResourceRequirements(requirements, options);
    }
    catch (err) {
        if (err instanceof Error) {
            throw new Error(`Invalid ${source} resource requirements ${sourceId}: ${err.message}`);
        }
        throw err;
    }
}
//# sourceMappingURL=resource-defaults.js.map